import { openDB } from 'idb';
import { DATA_VERSION, INBOX_CACHE_INVALIDATION_VERSION } from './version';

export class DataManager {
  constructor(accountId) {
    this.modelsToSync = ['inbox', 'label', 'team', 'canned_response'];
    this.accountId = accountId;
    this.db = null;
    this.dbPromise = null;
  }

  async initDb() {
    if (this.db) return this.db;
    if (this.dbPromise) return this.dbPromise;
    let rejectBlocked;
    let abandoned = false;
    const blocked = new Promise((_, reject) => { rejectBlocked = reject; });
    const timeout = setTimeout(() => rejectBlocked(new Error('Browser cache open timed out')), 2000);
    const dbName = `cw-store-${this.accountId}`;
    const opening = openDB(`cw-store-${this.accountId}`, DATA_VERSION, {
      blocked() {
        rejectBlocked(new Error('Browser cache upgrade is blocked by another tab'));
      },
      blocking: () => {
        this.db?.close();
        this.db = null;
        this.dbPromise = null;
      },
      terminated: () => { this.db = null; this.dbPromise = null; },
      upgrade(db, oldVersion, _newVersion, transaction) {
        // Existing databases already carry the stores added in earlier versions,
        // and createObjectStore throws on a name that is already taken.
        const createStore = (name, options) => {
          if (db.objectStoreNames.contains(name)) return;
          db.createObjectStore(name, options);
        };

        createStore('cache-keys');
        createStore('inbox', { keyPath: 'id' });
        createStore('label', { keyPath: 'id' });
        createStore('team', { keyPath: 'id' });
        createStore('canned_response', { keyPath: 'id' });
        if (oldVersion > 0 && oldVersion < INBOX_CACHE_INVALIDATION_VERSION) {
          transaction.objectStore('inbox').clear();
          transaction.objectStore('cache-keys').delete('inbox');
        }
      },
    });

    // A blocked open cannot be cancelled; close it if it succeeds after fallback.
    opening.then(db => {
      if (abandoned) { db.close(); this.dbPromise = null; }
    }).catch(() => { this.dbPromise = null; });
    this.dbPromise = Promise.race([opening, blocked]).then(db => {
      this.db = db;
      const dbNames = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
      if (!dbNames.includes(dbName)) {
        dbNames.push(dbName);
        localStorage.setItem('cw-idb-names', JSON.stringify(dbNames));
      }
      return db;
    }).catch(error => {
      abandoned = true;
      throw error;
    }).finally(() => { clearTimeout(timeout); });
    return this.dbPromise;
  }

  validateModel(name) {
    if (!name) throw new Error('Model name is not defined');
    if (!this.modelsToSync.includes(name)) {
      throw new Error(`Model ${name} is not defined`);
    }
    return true;
  }

  async replace({ modelName, data }) {
    this.validateModel(modelName);

    const tx = this.db.transaction(modelName, 'readwrite');
    await tx.store.clear();
    const rows = Array.isArray(data) ? data : [data];
    await Promise.all([...rows.map(item => tx.store.put(item)), tx.done]);
  }

  async push({ modelName, data }) {
    this.validateModel(modelName);

    if (Array.isArray(data)) {
      const tx = this.db.transaction(modelName, 'readwrite');
      data.forEach(item => {
        tx.store.add(item);
      });
      await tx.done;
    } else {
      await this.db.add(modelName, data);
    }
  }

  async get({ modelName }) {
    this.validateModel(modelName);
    return this.db.getAll(modelName);
  }

  async setCacheKeys(cacheKeys) {
    Object.keys(cacheKeys).forEach(async modelName => {
      this.db.put('cache-keys', cacheKeys[modelName], modelName);
    });
  }

  async getCacheKey(modelName) {
    this.validateModel(modelName);

    return this.db.get('cache-keys', modelName);
  }
}
